/**
 * PRD §9.5 — Candidate lifecycle (record-level) + intake / sourcing flows (CAN-015 / CAN-050).
 * Distinct from **application** pipeline status (§9.6) — one candidate may have many applications.
 */

import type { CandidateSource } from '@core/types';

/** UX phases aligned with PRD §9.5 mermaid (no separate DB column yet). */
export const CANDIDATE_LIFECYCLE_PHASES: readonly {
  key: string;
  title: string;
  description: string;
}[] = [
  {
    key: 'lead',
    title: 'Lead / sourced',
    description: 'Sourced or applied — record exists; not yet committed to an active pipeline stage.',
  },
  {
    key: 'active',
    title: 'Active',
    description: 'In process on one or more roles (applications). Primary work happens in §9.6 pipeline.',
  },
  {
    key: 'hired',
    title: 'Hired',
    description: 'Placed or offer accepted — align with application “placed” / offer states.',
  },
  {
    key: 'rejected',
    title: 'Rejected / ended',
    description: 'Closed for a given process; may re-enter as lead with consent / new mandate.',
  },
  {
    key: 'talent_pool',
    title: 'Talent pool',
    description: 'Nurture / consent-based pool for future reqs — tags and campaigns.',
  },
];

export const CANDIDATE_LIFECYCLE_HELPER =
  'Candidate lifecycle is the **person record**. Pipeline stage lives on **applications** (§9.6). Imports from LinkedIn, Naukri, colleges, or CSV/Excel create or update people; you can attach them to jobs and run **skill fitment** (overlap vs JD skills) per application.';

/** Sourcing / intake channels shown in wizard (maps to `source` + `source_channel`). */
export const CANDIDATE_INTAKE_CHANNELS: readonly {
  id: string;
  label: string;
  hint: string;
  source: CandidateSource;
  /** Stored in `source_channel` (TEXT) for reporting (CAN-015). */
  channelLabel: string;
}[] = [
  { id: 'linkedin', label: 'LinkedIn', hint: 'Sourced or inbound from LinkedIn', source: 'linkedin', channelLabel: 'LinkedIn' },
  { id: 'naukri', label: 'Naukri', hint: 'India job board traffic', source: 'other', channelLabel: 'Naukri' },
  { id: 'indeed', label: 'Indeed', hint: 'Board applications', source: 'job_board', channelLabel: 'Indeed' },
  { id: 'college', label: 'College / campus', hint: 'Campus drives, TPO lists', source: 'other', channelLabel: 'College / Campus' },
  { id: 'referral', label: 'Referral', hint: 'Employee / client referral', source: 'referral', channelLabel: 'Referral' },
  { id: 'job_board', label: 'Other job board', hint: 'Generic boards', source: 'job_board', channelLabel: 'Job Board' },
  { id: 'agency', label: 'Agency', hint: 'Partner agency submission', source: 'other', channelLabel: 'Agency' },
  { id: 'direct', label: 'Direct', hint: 'Email, walk-in, phone', source: 'manual', channelLabel: 'Direct' },
];

export const CANDIDATE_IMPORT_COLUMN_HINTS = `Expected columns (header row): full_name, email, phone, skills, current_company, current_title, location, linkedin_url. Aliases: name→full_name, e-mail→email. Skills can be comma-separated.`;

export const CANDIDATE_WIZARD_HIDE_KEY = 'ggj_b2b_hide_candidate_intake_wizard';
